// web/src/layouts/header/QuickDialBar.tsx —— 顶栏快捷拨号条（入口）：通话/拨号状态编排 + 组合。
// 展示分片见 web/src/layouts/header/QuickDialBar/ 目录（闲时拨号 / 通话控件 / 转接面板 / 加入黑名单弹窗 / 内部工具）。
// 既有调用方 import 路径与导出名不变（默认导出 QuickDialBar，props 仍为 { ringCallUUIDRef }）。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Space, Message } from '@arco-design/web-react';
import { IconPhone } from '@arco-design/web-react/icon';
import { useAgentStore } from '../../store/agent';
import { usePhoneStore } from '../../store/phone';
import { listCdrs } from '../../api/cdr';
import { maskPhone } from '../../utils/format';
import QuickDialIdlePane from './QuickDialBar/QuickDialIdlePane';
import QuickDialCallControls from './QuickDialBar/QuickDialCallControls';
import BlacklistModal from './QuickDialBar/BlacklistModal';
import CallbackModal from './QuickDialBar/CallbackModal';
import { DIAL_MAX_LEN, DIAL_REG, fmtMMSS, recentDialNumbers, resolveEchoReal } from './QuickDialBar/_dialHelpers';

// 通话实时记录已迁移至坐席工作台右栏「通话记录」tab（CallRecordPanel），全局弹框移除。

/**
 * 坐席通话信息条：BasicLayout 顶栏下方一行，由真实 phone.status 驱动。
 *
 * 设计原则：
 * - 仅坐席账号（agent.agentId）显示，与工作台「当前呼叫」卡互补但不重复——
 *   本条聚焦「尽快看见/操作」，工作台侧重「查看通话纪要/客户/转写」。
 * - 四种显示由真实通话状态映射：
 *   · 闲时（registered/idle/error/registering）：今日业务统计 + 快捷入口提示
 *   · 振铃（ringing-in/out）：号码（中间 4 位脱敏）+ 接听/拒接按钮（真实调 phone.answer / phone.hangup）
 *   · 通话中（in-call）：号码 + 客户名 + 通话计时 + 静音/保持/转接/挂断（真实 SIP 调用）
 *   · 结束通话后（in-call → 非 in-call 且本通已接通，盲转转出除外）：加入黑名单 / 预约回呼
 *
 * 「完成服务」= 关闭操作条回到闲时（best-effort 尝试同步后端坐席状态）。
 */

/**
 * 顶栏快捷拨号胶囊：号码输入 + 绿色呼出图标 + 通讯录图标（展开最近拨号历史）。
 * 通话中：号码框置灰展示当前主叫，按键发送 DTMF；通话控件（接听/转接/挂断）紧邻右侧。
 * 物理小键盘/全局键盘仍生效 → focus 后直接打字，无需先点击输入框。
 */
function QuickDialBar({ ringCallUUIDRef }: { ringCallUUIDRef: { current: string } }) {
  const navigate = useNavigate();
  const { status, remoteNumber, call, sendDtmf, hangup, answer, muted, held, toggleMute, toggleHold, startedAt, displayName, outboundEcho, clearOutboundEcho, healIfNeeded, consultPhase, consultDest, engine, completeConsultTransfer, cancelConsult, transferredOut } = usePhoneStore();
  const agentId = useAgentStore((s) => s.agentId);
  // 外部话机坐席（agent.phone_mode=desk）：工作台不注册软电话，来电在话机上振铃。
  // 这里把注册态提示换成只读说明，避免"未注册/重新注册"把坐席引向无法生效的自愈点击。
  const deskPhone = useAgentStore((s) => s.phoneMode) === 'desk';
  const [number, setNumber] = useState('');
  const [transferVisible, setTransferVisible] = useState(false);
  const [inviteVisible, setInviteVisible] = useState(false);
  useEffect(() => {
    if (consultPhase) setInviteVisible(false);
  }, [consultPhase]);
  // 最近拨号（按拨打时刻倒序去重，坐席高频外呼快捷回拨）
  const [history, setHistory] = useState<string[]>([]);
  /** 列表加载中（首次/刷新中，避免请求回来前先闪一次空态文案） */
  const [histLoading, setHistLoading] = useState(false);
  /** 请求在途标记：连续展开/收起时不并发重拉 */
  const histLoadingRef = useRef(false);
  const inCall = status === 'in-call';
  const isRinging = status === 'ringing-in' || status === 'ringing-out';
  const callActive = isRinging || inCall;
  const numberRef = useRef<any>(null);
  /** 本组件内刚完成盲转：本腿结束不做话后快照（客户已转出继续服务，无话后处理） */
  const justTransferredRef = useRef(false);
  /** 接听中（SIP accept → 桥接有 ~2s 延迟）：按钮 loading 防连点重复应答 */
  const [answering, setAnswering] = useState(false);
  /** 话后/历史回显会话：号码框显示中间 4 位脱敏文本，真实号码暂存于此，回拨时由 doCall 还原呼出 */
  const echoRealRef = useRef('');
  /**
   * 将一通真实号码回显到号码框（脱敏展示 + 暂存真实号）：话后回填/补 0 回显/历史选择统一走这里，
   * 保证坐席界面不出现完整号码；以后其它需要「回显可回拨号码」的入口可复用本函数。
   */
  const echoDialNumber = useCallback((real: string) => {
    const r = String(real ?? '').trim();
    if (!r) return;
    echoRealRef.current = r;
    setNumber(maskPhone(r));
  }, []);

  // ---------- 胶囊宽度平滑（输入 ⇄ 号码态切换不跳变） ----------
  const capRef = useRef<HTMLDivElement>(null);
  const prevCapWRef = useRef<number>();
  useEffect(() => {
    const el = capRef.current;
    if (!el) return;
    const final = el.offsetWidth;
    const prev = prevCapWRef.current;
    if (prev !== undefined && prev !== final && prev > 0) {
      el.style.transition = 'none';
      el.style.width = `${prev}px`;
      void el.offsetWidth;
      el.style.transition = 'width 0.22s ease';
      el.style.width = `${final}px`;
      const t = setTimeout(() => {
        el.style.width = '';
        el.style.transition = '';
      }, 240);
      prevCapWRef.current = final;
      return () => clearTimeout(t);
    }
    prevCapWRef.current = final;
    return undefined;
  }, [callActive]);

  // ---------- 最近拨号搜索 ----------
  const [histQuery, setHistQuery] = useState('');
  const filteredHistory = useMemo(() => {
    const q = histQuery.trim();
    if (!q) return history;
    return history.filter((n) => n.includes(q) || maskPhone(n).includes(q));
  }, [history, histQuery]);

  // 接听 loading 复位：接通（in-call）/ 呼叫消失（非振铃态）即复位；兜底 6s 超时
  useEffect(() => {
    if (!answering) return;
    if (status === 'in-call' || !isRinging) setAnswering(false);
    const t = setTimeout(() => setAnswering(false), 6000);
    return () => clearTimeout(t);
  }, [answering, status, isRinging]);

  // 挂断后保留的通话信息（号码 + 通话时长基准）：供顶部「加入黑名单」使用；
  // 新电话振铃时清除并切换为对应振铃/通话内容
  const [endedCall, setEndedCall] = useState<{ number: string; startedAt?: number } | null>(null);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  // 通话中实时快照（cleanupSession 挂断会清空 remoteNumber/startedAt，须在 in-call 期间用 ref 提前存好）
  const liveSnapshotRef = useRef<{ number: string; startedAt?: number }>({ number: '', startedAt: undefined });
  useEffect(() => {
    if (inCall && remoteNumber) {
      liveSnapshotRef.current = { number: remoteNumber, startedAt };
    }
  }, [inCall, remoteNumber, startedAt]);
  // 追踪「in-call → 非 in-call」的挂断/结束事件：接通过才保留结束态（ringing 未接不保留；
  // 盲转成功后的本腿结束不算完整通话——无话后动作，跳过快照直接回闲时）
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prev === 'in-call' && status !== 'in-call') {
      if (justTransferredRef.current) {
        justTransferredRef.current = false;
      } else {
        const snap = { ...liveSnapshotRef.current };
        setEndedCall(snap);
        // 挂断后把刚通话号码回填到号码输入框（中间 4 位脱敏，坐席不可见完整号码）；
        // 真实号码暂存 echoRealRef，点呼出时由 doCall 还原再拨，坐席免重输直接再呼出
        if (snap.number) echoDialNumber(snap.number);
      }
    }
    if (isRinging) {
      setEndedCall(null); // 新来电到来清除上一通结束态
      justTransferredRef.current = false;
    }
  }, [status, isRinging, echoDialNumber]);
  const inAfterCall = !inCall && !isRinging && !!endedCall;

  // 外呼「外地号自动补 0」回显：系统自动挂断恢复闲时后，号码框回显补 0 号（坐席免重输直接再呼出）。
  // 补 0 号同样是真实号码，统一走 echoDialNumber 脱敏回显——框内只露中间打码，真实号暂存供呼出还原
  useEffect(() => {
    if (!callActive && outboundEcho) {
      if (!number.trim()) echoDialNumber(outboundEcho);
      clearOutboundEcho();
    }
  }, [callActive, outboundEcho, clearOutboundEcho, number, echoDialNumber]);
  // 通话中计时（真实 in-call，mm:ss）
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (status !== 'in-call') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [status]);

  // 展开「最近拨号」时拉取本人最近呼出记录并去重（每次展开都重拉：CDR 在挂断时才写入，
  // 只拉一次会让本次会话刚拨的号码缺失或排在后面）
  const loadHistory = async () => {
    if (histLoadingRef.current) return;
    histLoadingRef.current = true;
    setHistLoading(true);
    try {
      const { list } = await listCdrs({ direction: 'outbound', agent_id: agentId, pageSize: 20 });
      setHistory(recentDialNumbers(list || []));
    } catch {
      /* 拦截器已提示，保留上一次列表 */
    } finally {
      histLoadingRef.current = false;
      setHistLoading(false);
    }
  };

  // 号码框键盘：空闲录入时数字/退格走浏览器原生编辑（尊重光标位置——光标移到前面输入
  // 即插入到前面，不再一律追加到末尾）；仅拦截「非法字符」与「已达上限仍无选区键入」；
  // 通话中聚焦时发 DTMF
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const k = e.key;
    if (status === 'in-call') {
      if (/^[0-9*#]$/.test(k)) {
        e.preventDefault();
        sendDtmf(k);
      }
      return;
    }
    // 复制/粘贴/全选等修饰键组合不拦截
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (/^[0-9*#]$/.test(k)) {
      // 已达 DIAL_MAX_LEN 且光标处无选中内容：拦截该键（避免继续往后追加/顶掉末尾字符）；
      // 有选中（替换）或未达上限时放行，交给原生插入到光标处
      const el = e.currentTarget;
      const start = el.selectionStart ?? number.length;
      const end = el.selectionEnd ?? start;
      if (number.length >= DIAL_MAX_LEN && start === end) {
        e.preventDefault();
      }
      return;
    }
    // 字母/符号等非法单字符直接拦截，不进入输入框（onChange 的过滤仅兜底粘贴场景）
    if (k.length === 1) {
      e.preventDefault();
    }
  };

  // 全页键盘捕获：空闲且焦点不在其他输入框时，物理数字键直接进号码框（免先点输入框）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // 呼入/呼出/通话中：号码输入框隐藏（无 DTMF 数字键采集），避免物理按键静默累积成未知号码
      if (status === 'in-call' || status === 'ringing-in' || status === 'ringing-out') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        setNumber((n) => `${n}${e.key}`.slice(0, DIAL_MAX_LEN));
        numberRef.current?.focus();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setNumber((n) => n.slice(0, -1));
        numberRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [status]);

  const doCall = () => {
    // 号码框可能为「话后/历史回显的脱敏号」：命中回显映射时还原背后的真实号码呼出（坐席看不到完整号码）
    const t = resolveEchoReal(number, echoRealRef.current);
    if (!t) return;
    if (!DIAL_REG.test(t)) {
      Message.warning('请输入正确的电话号码');
      return;
    }
    void call(t);
    echoRealRef.current = ''; // 呼出后清除回显映射，避免与后续手动输入串号
    setNumber('');
  };

  // 接听：置 loading 防连点；SIP accept 到桥接完成约 2s，期间按钮不可重复应答
  const handleAnswer = async () => {
    if (answering) return;
    setAnswering(true);
    navigate('/workbench'); // 接听后跳转坐席工作台
    try {
      await answer();
    } catch {
      /* 拦截器已提示 */
    }
  };

  // 号码框输入（闲时）：坐席手动编辑即解除回显映射，此后按手动输入呼出；只留数字/*/#
  const onNumberInput = (v: string) => {
    echoRealRef.current = '';
    setNumber(v.replace(/[^0-9*#]/g, '').slice(0, DIAL_MAX_LEN));
  };

  // 取消外呼/拒接/挂断共用：先复位「刚完成盲转」标记再挂断
  const onHangup = () => {
    justTransferredRef.current = false;
    void hangup();
  };

  return (
    <>
      <Space size={8} align="center" className="bcc-quick-dial-ctl">
        <div ref={capRef} className={`bcc-header-capsule bcc-quick-dial${inCall ? ' bcc-quick-dial-in-call' : ''}${isRinging ? ' bcc-quick-dial-ringing' : ''}`}>
        {callActive ? (
          <div className="bcc-quick-dial-info">
            <span className="bcc-quick-dial-label">
              {isRinging && <IconPhone className="bcc-ring-icon" />}
              {status === 'in-call'
                ? consultPhase === 'ringing'
                  ? '邀请中'
                  : consultPhase === 'up'
                    ? '咨询中'
                    : '通话中'
                : status === 'ringing-in'
                  ? '来电'
                  : '外呼中'}
            </span>
            <span className="bcc-quick-dial-num">{maskPhone(remoteNumber) || '—'}</span>
            {/* 客户名 = 完整号码（未补全的自动建档客户）时不再重复展示，仅保留左侧脱敏号 */}
            {displayName && !/^[\d\s\-+*#]+$/.test(displayName.trim()) && (
              <span>{displayName}</span>
            )}
            {consultDest && consultPhase && (
              <span className="bcc-quick-dial-meta">{consultPhase === 'ringing' ? `邀请 ${consultDest}` : `咨询 ${consultDest}`}</span>
            )}
            {inCall && <span className="bcc-quick-dial-meta">{fmtMMSS(startedAt, now)}</span>}
          </div>
        ) : (
          <QuickDialIdlePane
            deskPhone={deskPhone}
            status={status}
            number={number}
            numberRef={numberRef}
            history={history}
            histLoading={histLoading}
            histQuery={histQuery}
            filteredHistory={filteredHistory}
            onHistQueryChange={setHistQuery}
            onNumberInput={onNumberInput}
            onInputKeyDown={onInputKeyDown}
            onCall={doCall}
            onEchoNumber={echoDialNumber}
            onLoadHistory={loadHistory}
            onHeal={() => void healIfNeeded()}
            inAfterCall={inAfterCall}
            onOpenBlacklist={() => setBlacklistOpen(true)}
            onOpenCallback={() => setCallbackOpen(true)}
          />
        )}
      </div>

      {callActive && (
        <QuickDialCallControls
          status={status}
          answering={answering}
          muted={muted}
          held={held}
          transferVisible={transferVisible}
          onTransferVisibleChange={setTransferVisible}
          onAnswer={() => void handleAnswer()}
          onHangup={onHangup}
          onToggleMute={() => void toggleMute()}
          onToggleHold={() => void toggleHold()}
          ringCallUUIDRef={ringCallUUIDRef}
          onTransferred={() => {
            justTransferredRef.current = true;
          }}
          consultPhase={consultPhase}
          consultDest={consultDest}
          canConsult={engine === 'zitch' && !transferredOut}
          transferredOut={!!transferredOut}
          inviteVisible={inviteVisible}
          onInviteVisibleChange={setInviteVisible}
          onCompleteConsult={() => {
            justTransferredRef.current = true;
            void completeConsultTransfer();
          }}
          onCancelConsult={() => void cancelConsult()}
        />
      )}
      </Space>
      {inAfterCall && (
        <>
          <BlacklistModal visible={blacklistOpen} number={endedCall?.number || ''} onClose={() => setBlacklistOpen(false)} />
          <CallbackModal visible={callbackOpen} number={endedCall?.number || ''} onClose={() => setCallbackOpen(false)} />
        </>
      )}
    </>
  );
}

export default QuickDialBar;
